import { memo, useCallback, useEffect, useState } from "react";
import Card from "./components/ui/Card";
import Button from "./components/ui/Button";
import BaseTemplate from "./templates/BaseTemplate";
import { getDiagnosticPattern } from "./api";
import { NavLink, useNavigate, useParams } from "react-router";
import { ChevronLeft, Plus } from "lucide-react";
import Badge from "./components/ui/Badge";
import BadgeBrainRegion from "./components/ui/BadgeBrainRegion";
import clsx from "clsx";
import { getUniqueBrainAreasFromChannels } from "./lib/utils";

export const BrainRegionText = memo(
  ({ channel, children, coloredBackground = false, className }) => {
    const brainAreas = {
      frontal: ["Fp1", "Fp2", "F3", "F4", "F7", "F8", "Fz"],
      central: ["C3", "C4", "Cz"],
      temporal: ["T3", "T4", "T5", "T6"],
      parietal: ["P3", "P4", "Pz"],
      occipital: ["O1", "O2"],
    };

    let area = "";

    for (const brainArea in brainAreas) {
      if (
        brainAreas[brainArea].some(
          (v) => v.toUpperCase() === channel.toUpperCase()
        )
      ) {
        area = brainArea;
        break;
      }
    }

    const regions = {
      prefrontal: "yellow",
      frontal: "red",
      central: "violet",
      parietal: "green",
      temporal: "cyan",
      occipital: "blue",
    };

    const variants = {
      default: "text-gray-700",
      yellow: "text-yellow-700",
      red: "text-red-700",
      green: "text-green-700",
      cyan: "text-cyan-700",
      blue: "text-blue-700",
      violet: "text-violet-700",
    };

    return coloredBackground ? (
      <Badge variant={clsx(regions[area])} className={clsx(className)}>
        {children}
      </Badge>
    ) : (
      <span
        className={clsx(variants[regions[area]], "font-semibold", className)}
      >
        {children}
      </span>
    );
  }
);

const DiagnosticsPatternDetail = () => {
  const [pattern, setPattern] = useState([]);
  const [interpretationThemes, setInterpretationThemes] = useState([]);
  const { patternId } = useParams();
  let navigate = useNavigate();

  const brainAreas = getUniqueBrainAreasFromChannels(
    pattern?.involved_channels ?? []
  );

  const fetchPatterns = useCallback(async () => {
    try {
      const response = await getDiagnosticPattern(patternId);
      setPattern(response.data || []);
      setInterpretationThemes(response.data.interpretation_themes || []);
    } catch (err) {
      console.error("Error fetching patients:", err);
      // setError(err.response?.data?.detail || "Failed to fetch patients");
      setPattern([]);
    }
  }, [patternId]);

  useEffect(() => {
    fetchPatterns();
  }, [fetchPatterns]);

  const onAssociateTheme = (id) => {
    navigate(`/interpretation-themes/associate/${id}`);
  };

  return (
    <BaseTemplate>
      <Card name={"Diagnostics Pattern"}>
        <div className="flex flex-col gap-4">
          <div className="flex flex-col gap-1">
            <h3 className="text-base md:text-lg font-semibold text-[#1c3f70]">
              Pattern: {pattern.pattern_signature}
            </h3>
            <p className="text-xs text-gray-400">ID: {patternId}</p>
          </div>
          <div className="flex">
            <NavLink
              to={"/diagnostics-center/"}
              className={
                "flex flex-row items-center text-sm text-gray-500 hover:text-gray-400 focus-visible:text-gray-400 transition-[color,translate] hover:translate-x-2 focus-visible:translate-x-2"
              }
            >
              <ChevronLeft size={"16"} />
              <span className="">Back to Diagnostics Center</span>
            </NavLink>
          </div>
          <Card name={"Pattern Details"} variant="secondary">
            {pattern.description}
          </Card>
          <Card name={"Involved Brain Areas"} variant="secondary">
            <div className="flex flex-row gap-2">
              {brainAreas.map((brainArea) => (
                <BadgeBrainRegion key={brainArea} region={brainArea} />
              ))}
            </div>
          </Card>
          <Card name={"Involved Channels and Bands"} variant="secondary">
            <div>
              <p>
                Channels:{" "}
                {pattern.involved_channels?.map((channel) => (
                  <BrainRegionText key={channel} channel={channel}>
                    {`${channel} `}
                  </BrainRegionText>
                ))}
              </p>
              <p>
                Bands:{" "}
                {pattern.involved_bands?.map((band) => (
                  <span key={band}>{`${band} `}</span>
                ))}
              </p>
            </div>
          </Card>
          <Card name={"Occurences"} variant="secondary">
            <div>Suggested: {pattern.suggested_count}</div>
            <div>Confirmed: {pattern.confirmed_count}</div>
            <div>
              Last seen:{" "}
              {pattern.last_detected_at
                ? new Date(pattern.last_detected_at).toLocaleDateString()
                : "N/A"}
            </div>
          </Card>
          <Card name={"Associated Interpretation Themes"} variant="secondary">
            <div className="flex flex-col gap-2">
              {interpretationThemes.length > 0 ? (
                <ul className="flex flex-col gap-2">
                  {interpretationThemes.map((theme) => (
                    <li key={theme.id}>
                      <NavLink to={`/interpretation-themes/themes/${theme.id}`}>
                        <Card
                          name={
                            <div>
                              {/* <p>{theme.pattern_signature}</p> */}
                              <p className="text-xs text-gray-400 font-normal">
                                ID: {theme.id}
                              </p>
                            </div>
                          }
                          variant="secondary"
                        >
                          <div className="flex flex-col gap-2">
                            <p>{theme.interpretation_text}</p>
                            <div className="flex gap-2 flex-wrap">
                              {theme.keywords.map((keyword) => (
                                <Badge key={keyword}>{keyword}</Badge>
                              ))}
                            </div>
                            <div>
                              <p>
                                Channels:{" "}
                                {theme.theme_involved_channels.map(
                                  (channel) => (
                                    <BrainRegionText
                                      key={channel}
                                      channel={channel}
                                    >
                                      {`${channel} `}
                                    </BrainRegionText>
                                  )
                                )}
                              </p>
                              <p>
                                Bands:{" "}
                                {theme.theme_involved_bands.map((band) => (
                                  <span key={band}>{`${band} `}</span>
                                ))}
                              </p>
                            </div>
                          </div>
                        </Card>
                      </NavLink>
                    </li>
                  ))}
                </ul>
              ) : (
                <span>No Associated Interpretation Themes</span>
              )}
              <Button
                onClick={() => onAssociateTheme(patternId)}
                className="text-white px-3 py-1 md:px-4 md:py-1.5 self-end"
              >
                <Plus />
                Associate Theme
              </Button>
            </div>
          </Card>
          {/* <Card name={"Recent Detections"} variant="secondary">
            <ul>
              {[
                {
                  name: "Kasia",
                  id: "ca0dd0b8-cc06-44df-9f1e-21698a53c3d3",
                  date: "25.05.2025, 20:05:35",
                },
              ].map(({ name, id, date }) => (
                <SessionItem key={id} name={name} id={id} date={date} />
              ))}
            </ul>
          </Card> */}
        </div>
      </Card>
    </BaseTemplate>
  );
};

// const SessionItem = ({ name, id, date }) => {
//   return (
//     <li>
//       <Card name={name} variant="secondary" className={"text-base"}>
//         <p className="text-gray-400">{date}</p>
//         <p>Session ID: {id}</p>
//       </Card>
//     </li>
//   );
// };

export default DiagnosticsPatternDetail;
